import { ArdoqId, WidgetDataSourceTypes, WidgetTypes } from '@ardoq/api-types';
import { Locale } from '@ardoq/locale';
import { GaugeWidgetRenderData } from '../renderTypes';
import { DashboardModes, OnWidgetDrilldown } from '../../types';
import { PieChartClickHandler } from '../categoricalCharts/EChart';
import { WidgetLinkedDashboardFilter } from '../types';
import { widgetOperations } from '../operations';
import { GaugeWidgetProps } from './types';
import type { I18nLocale } from '@ardoq/i18n';

type GetGaugeWidgetPropsParams = {
  widgetRenderData: GaugeWidgetRenderData;
  userLocale: Locale;
  dashboardMode: DashboardModes;
  onWidgetDrilldown?: OnWidgetDrilldown;
  activeLinkedDashboardFilters?: WidgetLinkedDashboardFilter[];
  dashboardId?: ArdoqId;
  i18nLocale: I18nLocale;
};

const getGaugeClickHandler = ({
  widgetRenderData,
  dashboardMode,
  onWidgetDrilldown,
  activeLinkedDashboardFilters = [],
  dashboardId,
}: Omit<GetGaugeWidgetPropsParams, 'userLocale' | 'i18nLocale'>):
  | PieChartClickHandler
  | undefined => {
  const { datasource, filters, condition, permissions } = widgetRenderData;

  const canDrilldown =
    dashboardMode === DashboardModes.VIEW &&
    datasource?.sourceType === WidgetDataSourceTypes.REPORT &&
    Boolean(permissions?.canExploreDatasource);

  if (!onWidgetDrilldown || !datasource || !condition || !canDrilldown)
    return undefined;

  return ({ data }) => {
    if (data.isOtherSegment) return;
    onWidgetDrilldown({
      datasource,
      filters: {
        ...widgetOperations.mergeDashboardAndWidgetFilters(
          filters ?? {},
          activeLinkedDashboardFilters
        ),
        ...condition,
      },
      widgetType: WidgetTypes.GAUGE,
      dashboardId,
    });
  };
};

export const getGaugeWidgetProps = ({
  widgetRenderData,
  userLocale,
  dashboardMode,
  onWidgetDrilldown,
  activeLinkedDashboardFilters,
  dashboardId,
  i18nLocale,
}: GetGaugeWidgetPropsParams): GaugeWidgetProps & {
  viewType: WidgetTypes.GAUGE;
} => {
  const { data } = widgetRenderData;
  const { numerator, denominator } = data ?? { numerator: 0, denominator: 0 };

  const clickHandler = getGaugeClickHandler({
    widgetRenderData,
    dashboardMode,
    onWidgetDrilldown,
    activeLinkedDashboardFilters,
    dashboardId,
  });

  return {
    numerator,
    denominator,
    userLocale,
    clickHandler,
    i18nLocale,
    viewType: WidgetTypes.GAUGE,
  };
};
